import { Link } from "react-router-dom";
import { Mail, Instagram } from "lucide-react";

const Section = ({ n, title, children }) => (
  <section className="border-t border-[#DCD5CA] pt-8 md:pt-10 mt-8 md:mt-10 first:border-t-0 first:mt-0 first:pt-0">
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-10">
      <div className="md:col-span-3">
        <p className="overline">Section {n}</p>
      </div>
      <div className="md:col-span-9">
        <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-[#2A2626] leading-tight">
          {title}
        </h2>
        <div className="mt-4 md:mt-5 text-[15px] leading-[1.85] text-[#5C564F] space-y-4">
          {children}
        </div>
      </div>
    </div>
  </section>
);

export default function ReturnExchangePolicy() {
  return (
    <div data-testid="return-policy-page" className="bg-[#FAF8F5]">
      {/* Hero band */}
      <section className="border-b border-[#DCD5CA] bg-[#F0EBE1]/60">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 py-14 md:py-20">
          <p className="overline">Care · Policies</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] text-[#2A2626]">
            Return &amp; Exchange<br />
            <span className="italic font-light">Policy.</span>
          </h1>
          <p className="mt-6 text-[15px] md:text-base text-[#5C564F] max-w-2xl leading-relaxed">
            At <strong className="text-[#2A2626]">Innayaa</strong>, each product is carefully
            handcrafted with extreme care and devotion. Because our products are handmade,
            every piece is unique, and minor variations in design, texture, or colour are
            natural characteristics of the craft rather than defects. Please read our
            Return &amp; Exchange Policy carefully before placing an order.
          </p>
        </div>
      </section>

      {/* Policy body */}
      <section className="max-w-[1100px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 py-14 md:py-20">
        <Section n="1" title="No Refund Policy">
          <p>
            Due to the custom and handcrafted nature of our products,
            {" "}
            <strong className="text-[#2A2626]">
              we strictly do not offer refunds
            </strong>
            {" "}
            once an order has been placed and delivered. We encourage you to review
            product details, sizing, and descriptions carefully before completing
            your purchase.
          </p>
        </Section>

        <Section n="2" title="Eligibility for Exchanges">
          <p>We only offer product exchanges under the following specific conditions:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong className="text-[#2A2626]">Transit Damage:</strong>{" "}
              If the product you received is damaged during shipping.
            </li>
            <li>
              <strong className="text-[#2A2626]">Incorrect Item Received:</strong>{" "}
              If you received a different product or size from what you ordered.
            </li>
          </ul>
          <p className="italic">
            Note: Exchanges are subject to inventory availability. If the item is
            out of stock, we will assist you with an alternative solution or store credit.
          </p>
        </Section>

        <Section n="3" title="Timeframe to Raise a Request">
          <ul className="list-disc pl-5 space-y-2">
            <li>
              You must initiate a return or exchange request within{" "}
              <strong className="text-[#2A2626]">48 hours of delivery</strong>.
            </li>
            <li>
              Requests raised after 48 hours of delivery will unfortunately{" "}
              <strong className="text-[#2A2626]">not be eligible</strong> for exchange.
            </li>
          </ul>
        </Section>

        <Section n="4" title="How to Request an Exchange">
          <p>To initiate an exchange request, please follow these steps:</p>
          <ol className="list-decimal pl-5 space-y-3">
            <li>
              <strong className="text-[#2A2626]">Unboxing Video / Photos:</strong>{" "}
              Take clear photos and an unboxing video showing the damaged product
              or incorrect item along with the original packaging and invoice.
            </li>
            <li>
              <strong className="text-[#2A2626]">Contact Us:</strong> Send the images
              / video along with your <em>Order Number</em> and a description of
              the issue to:
              <ul className="list-disc pl-5 mt-2 space-y-1.5">
                <li>
                  Email:{" "}
                  <a
                    href="mailto:innayaaofficial@gmail.com"
                    className="text-[#2A2626] link-underline"
                  >
                    innayaaofficial@gmail.com
                  </a>
                </li>
                <li>
                  WhatsApp:{" "}
                  <a
                    href="https://wa.me/918240044327"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#2A2626] link-underline"
                  >
                    +91 82400 44327
                  </a>
                </li>
                <li>
                  Phone:{" "}
                  <a
                    href="tel:+918240044327"
                    className="text-[#2A2626] link-underline"
                  >
                    +91 82400 44327
                  </a>
                </li>
              </ul>
            </li>
            <li>
              <strong className="text-[#2A2626]">Verification:</strong> Our team
              will review your request within 24–48 hours and notify you if your
              exchange request is approved.
            </li>
          </ol>
        </Section>

        <Section n="5" title="Exchange Process & Delivery Timeline">
          <ul className="list-disc pl-5 space-y-2">
            <li>
              Once your exchange request is approved, our team will guide you
              through returning the damaged / wrong product.
            </li>
            <li>
              The item being returned must be{" "}
              <strong className="text-[#2A2626]">unused, in its original condition</strong>,
              with all tags and original packaging intact.
            </li>
            <li>
              <strong className="text-[#2A2626]">Delivery Timeline:</strong>{" "}
              Replacement products will be dispatched after quality inspection
              and delivered to your shipping address within{" "}
              <strong className="text-[#2A2626]">5 to 7 working days</strong>{" "}
              from the date of approval.
            </li>
          </ul>
        </Section>

        <Section n="6" title="Non-Eligible Scenarios">
          <p>Exchanges will not be accepted for:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Products damaged due to improper use, handling, or wear and tear after delivery.</li>
            <li>Minor natural variations inherent to handmade products.</li>
            <li>Requests made after the 48-hour delivery window.</li>
          </ul>
        </Section>
      </section>

      {/* Contact block */}
      <section className="bg-[#F0EBE1]/70 border-t border-[#DCD5CA]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 py-14 md:py-20 text-center">
          <p className="overline">Contact Us</p>
          <h2 className="mt-4 font-serif text-3xl md:text-4xl tracking-tight text-[#2A2626]">
            Any questions? We&rsquo;re here.
          </h2>
          <p className="mt-5 text-[15px] text-[#5C564F] max-w-lg mx-auto">
            If you have any questions or require assistance regarding our
            Return &amp; Exchange Policy, feel free to reach out.
          </p>

          <div className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-5 md:gap-8 text-[#2A2626]">
            <a
              data-testid="policy-email-link"
              href="mailto:innayaaofficial@gmail.com"
              className="inline-flex items-center gap-2.5 font-serif text-lg md:text-xl hover:opacity-70"
            >
              <Mail size={18} strokeWidth={1.5} />
              innayaaofficial@gmail.com
            </a>
            <a
              href="https://www.instagram.com/innayaa.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 font-serif text-lg md:text-xl hover:opacity-70"
            >
              <Instagram size={18} strokeWidth={1.5} />
              @innayaa.in
            </a>
          </div>

          <div className="mt-10">
            <Link
              to="/shop"
              className="inline-block bg-[#2A2626] text-[#FAF8F5] px-9 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-[#3A3632] transition-colors"
            >
              Back to shop
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
